import React, { useState } from "react";
import { Tabs, Paper, Stack, Text, Alert, Group, Button } from "@mantine/core";
import {
  IconEdit,
  IconUpload,
  IconFileText,
  IconDownload,
  IconTrash,
} from "@tabler/icons-react";
import { TorSpecificationEditor } from "./TorSpecificationEditor";
import { TorDocumentUpload } from "./TorDocumentUpload";
import {
  useGetTorDocument,
  useDeleteTorDocument,
  useDownloadTorDocument,
} from "../hooks/useTorDocuments";
import { useSubmitForApproval } from "../hooks/useApproval";
import { useAuth } from "../../../auth/AuthContext";
import { useQueryClient } from "@tanstack/react-query";

interface TorTabProps {
  tenderId: string;
  torSpecification?: string | null;
  tenderStage?: string;
  onSpecificationUpdate?: (content: string) => void;
}

export const TorTab: React.FC<TorTabProps> = ({
  tenderId,
  torSpecification,
  tenderStage = "DRAFT",
  onSpecificationUpdate,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string | null>("specification");
  const { data: torDocument, isLoading: isLoadingDocument } =
    useGetTorDocument(tenderId);
  const deleteTorDocument = useDeleteTorDocument(tenderId);
  const downloadTorDocument = useDownloadTorDocument(tenderId);
  const submitForApproval = useSubmitForApproval(tenderId);

  const isProcurementOfficer = user?.role === "PROCUREMENT_OFFICER";
  const isDraftStage = tenderStage === "DRAFT";
  const canEdit = isProcurementOfficer && isDraftStage;
  const hasTorDocument =
    torDocument && !isLoadingDocument && !deleteTorDocument.isPending;

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Text size="xl" fw={600}>
          Terms of Reference (TOR)
        </Text>

        {!isDraftStage && (
          <Alert color="yellow" variant="light">
            <Text size="sm">
              This tender is in <strong>{tenderStage}</strong> stage and cannot
              be edited. Only tenders in DRAFT stage can be modified.
            </Text>
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab
              value="specification"
              leftSection={<IconEdit size={16} />}
              disabled={!canEdit}
            >
              Specification Editor
            </Tabs.Tab>
            <Tabs.Tab
              value="document"
              leftSection={<IconUpload size={16} />}
              disabled={!canEdit}
            >
              Document Upload
            </Tabs.Tab>
            <Tabs.Tab value="view" leftSection={<IconFileText size={16} />}>
              View TOR
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="specification" pt="md">
            <Stack gap="md">
              <Alert color="blue" variant="light">
                <Text size="sm">
                  Use the rich text editor below to create or edit the TOR
                  specification. You can format text, add lists, links, and
                  images.
                </Text>
              </Alert>

              <TorSpecificationEditor
                tenderId={tenderId}
                initialContent={torSpecification || ""}
                onSave={onSpecificationUpdate}
                readOnly={!canEdit}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="document" pt="md">
            <Stack gap="md">
              <Alert color="blue" variant="light">
                <Text size="sm">
                  Upload a PDF, DOC, or DOCX file containing the complete Terms
                  of Reference document. This will be available for bidders to
                  download.
                </Text>
              </Alert>

              <TorDocumentUpload
                tenderId={tenderId}
                onUploadSuccess={() => {
                  // Refresh the document data by invalidating the query
                  queryClient.invalidateQueries({
                    queryKey: ["torDocument", tenderId],
                  });
                }}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="view" pt="xl">
            <Stack gap="md">
              {deleteTorDocument.isPending ? (
                <Alert color="blue" variant="light">
                  <Text size="sm">
                    <strong>Deleting TOR document...</strong> Please wait while
                    the document is being removed.
                  </Text>
                </Alert>
              ) : hasTorDocument ? (
                <Alert color="green" variant="light">
                  <Text size="sm">
                    <strong>TOR Document Available:</strong>{" "}
                    {torDocument.fileName}
                  </Text>
                  <Text size="sm">
                    <strong>Size:</strong>{" "}
                    {(torDocument.fileSize / 1024 / 1024).toFixed(2)} MB
                  </Text>
                  <Text size="sm">
                    <strong>Uploaded:</strong>{" "}
                    {new Date(torDocument.uploadedAt).toLocaleDateString()}
                  </Text>

                  <Group mt="sm">
                    <Button
                      size="sm"
                      leftSection={<IconDownload size={16} />}
                      variant="light"
                      loading={downloadTorDocument.isPending}
                      onClick={() => {
                        downloadTorDocument.mutate();
                      }}
                    >
                      Download Document
                    </Button>
                    {canEdit && (
                      <Button
                        size="sm"
                        leftSection={<IconTrash size={16} />}
                        variant="light"
                        color="red"
                        loading={deleteTorDocument.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you want to delete this TOR document? This action cannot be undone."
                            )
                          ) {
                            deleteTorDocument.mutate();
                          }
                        }}
                      >
                        Delete Document
                      </Button>
                    )}
                  </Group>
                </Alert>
              ) : (
                <Alert color="yellow" variant="light">
                  <Text size="sm">
                    No TOR document has been uploaded yet. Procurement officers
                    can upload a document using the "Document Upload" tab.
                  </Text>
                </Alert>
              )}

              {torSpecification && (
                <Paper p="md" withBorder>
                  <Text size="lg" fw={500} mb="md">
                    TOR Specification
                  </Text>
                  <div
                    dangerouslySetInnerHTML={{ __html: torSpecification }}
                    style={{
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      padding: "12px",
                      backgroundColor: "#f8f9fa",
                    }}
                  />
                </Paper>
              )}

              {/* Submit for Approval Section */}
              {canEdit && tenderStage === "DRAFT" && (
                <Paper p="md" withBorder>
                  <Text size="lg" fw={500} mb="md">
                    Submit for Approval
                  </Text>
                  <Text size="sm" color="dimmed" mb="md">
                    Once you have completed the TOR specification and/or uploaded the TOR document, 
                    you can submit this tender for approval by the Head of Procurement.
                  </Text>
                  
                  <Button
                    color="blue"
                    loading={submitForApproval.isPending}
                    onClick={() => {
                      if (window.confirm(
                        "Are you sure you want to submit this tender for approval? This action cannot be undone and the tender will be locked for editing."
                      )) {
                        submitForApproval.mutate();
                      }
                    }}
                    disabled={!hasTorDocument && !torSpecification}
                  >
                    Submit for Approval
                  </Button>
                  
                  {!hasTorDocument && !torSpecification && (
                    <Text size="xs" color="red" mt="xs">
                      You must have either a TOR specification or TOR document before submitting for approval.
                    </Text>
                  )}
                </Paper>
              )}

              {/* Approval Status Display */}
              {tenderStage === "PENDING_APPROVAL" && (
                <Alert color="yellow" variant="light">
                  <Text size="sm">
                    <strong>Pending Approval:</strong> This tender has been submitted for approval 
                    and is awaiting review by the Head of Procurement.
                  </Text>
                </Alert>
              )}

              {tenderStage === "PUBLISHED" && (
                <Alert color="green" variant="light">
                  <Text size="sm">
                    <strong>Approved and Published:</strong> This tender has been approved and is now published.
                  </Text>
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Paper>
  );
};
